// @givmo/app-admin — Employer gift-cards page (EP-WEB4). ★ MONEY-MOVING ENTRY ★
//
// A company sends a company-funded gift card to a roster employee (D-EP-5, Member-level — NO Owner
// gate). This page is BOTH the sent-list (the main body) AND the compose flow (recipient → amount +
// message → funding → the money-moving confirm dialog). It clones the merged /give flow (the house
// template) for its funding picker, its page-owned signature-keyed idempotency holder, and its
// confirm-before-charge dialog discipline.
//
// DARK: the whole surface is gated by `isCompanyGiftCardsEnabled()` (VITE_COMPANY_GIFT_CARDS_ENABLED,
// default off). The `/gift-cards` route redirects when off; this page ALSO redirects (defense in depth)
// so the flow is unreachable until the switch flips in lockstep with the backend
// `COMPANY_GIFT_CARDS_ENABLED` flag (which 404s the two endpoints while off).
//
// PERMISSION (D-EP-5): Member-level — any admin holding `company:send_gift_cards`. This page reads NO
// role and applies NO Owner gate; the backend authorizes every send. Members cannot deposit, so the
// wallet-insufficient copy points to an Owner rather than offering a deposit action.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { sdk, type CompanyGiftCardOut, isGivmoError } from '@givmo/api-client';
import { toDateOnly } from '@givmo/api-client/list';
import {
  companiesGetCompanyCardsV2Options,
  companiesGetCompanyWalletOptions,
  companiesGetCompanyWalletQueryKey,
} from '@givmo/api-client/query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  TextField,
  testIdProp,
  toast,
  type DataTableColumn,
  type SelectOption,
} from '@givmo/design-system';
import { Gift, Users, Wallet as WalletIcon } from 'lucide-react';
import { isCompanyGiftCardsEnabled, isCompanyOccasionProgramsEnabled } from '../../lib/env';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { cardLabel, extractCards } from '../settings/payment-methods-decode';
import {
  extractCompanyGiftCards,
  formatGiftCardAmount,
  formatGiftCardCharged,
  formatGiftCardDollars,
  fundingDisplayLabel,
  fundingLabel,
  fundingSummaryLabel,
  parseGiftCardAmount,
  toWalletFunds,
  walletCoversAmount,
  GIFT_CARD_PRESETS,
  GIFT_CARD_RANGE_LABEL,
  type Funding,
  type GiftCardRecipient,
} from './gift-cards-decode';
import {
  batchFaceTotalDollars,
  GIFT_CARD_BATCH_MAX_COUNT,
  type BatchRecipient,
} from './gift-cards-batch-decode';
import { createGiftCardIdempotencyKeyHolder } from './gift-card-idempotency';
import { createGiftCardBatchIdempotencyKeyHolder } from './gift-card-batch-idempotency';
import { GiftCardRecipientPicker } from './recipient-picker';
import { GiftCardStatusCell } from './gift-card-status-cell';
import { SendGiftCardDialog } from './send-gift-card-dialog';
import { BulkComposePanel } from './bulk-compose-panel';
import { SendGiftCardBatchDialog } from './send-gift-card-batch-dialog';
import { BatchReport } from './batch-report';
import { OccasionProgramsPanel } from './occasion-programs-panel';

const WALLET_VALUE = 'wallet' as const;
const MESSAGE_MAX = 500;
const NAME_MAX = 200;
const SENT_PAGE_LIMIT = 10;
const SENT_LIST_KEY = ['companies-list_company_gift_cards', 'sent'] as const;
// Prefix-match key for invalidating the batches report list after a batch send (the report keys under
// `['companies-list_company_gift_card_batches', 'report']`).
const BATCHES_LIST_KEY_PREFIX = ['companies-list_company_gift_card_batches'] as const;
type ComposeMode = 'single' | 'bulk' | null;
type ReportTab = 'individual' | 'batches' | 'programs';

async function fetchSentPage(offset: number, signal: AbortSignal): Promise<CompanyGiftCardOut[]> {
  const { data } = await sdk.companiesListCompanyGiftCards({
    query: { 'page[limit]': SENT_PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractCompanyGiftCards(data);
}

export function GiftCardsPage() {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const enabled = isCompanyGiftCardsEnabled();

  // Defense in depth: even though the /gift-cards route redirects when off, bounce here too so the
  // flow can never paint while dark (e.g. if the route gate ever regresses).
  useEffect(() => {
    if (!enabled) void navigate({ to: paths.dashboard });
  }, [enabled, navigate, paths.dashboard]);
  if (!enabled) return null;

  return <GiftCardsFlow />;
}

function GiftCardsFlow() {
  const queryClient = useQueryClient();

  // The Programs tab is gated INDEPENDENTLY of this page's own switch (EP-BE8-web): the two flip
  // separately, and the backend's occasion routes 404 while ITS flag is off, so the tab renders only
  // when the occasion switch is on and its panel treats a 404 as "not available here".
  const occasionProgramsEnabled = isCompanyOccasionProgramsEnabled();

  // ── Compose state (single XOR bulk XOR none) ──
  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [activeTab, setActiveTab] = useState<ReportTab>('individual');
  // Set when the Programs tab hands a batch off to the Batches tab (an occasion gift → the batch that
  // paid for it). The page owns it because the hand-off crosses two sibling tabs.
  //
  // ★ It MUST be cleared once BatchReport has applied it. `Tabs.Panel` unmounts an inactive panel, so a
  // lingering id is re-read on the next mount and every later visit to Batches would re-open that stale
  // batch's detail instead of the list. `clearFocusBatch` is the one-shot release; it is `useCallback`'d
  // because BatchReport takes it as an effect dependency.
  const [focusBatchId, setFocusBatchId] = useState<string | null>(null);
  const clearFocusBatch = useCallback(() => setFocusBatchId(null), []);

  // Single-send compose state.
  const [recipient, setRecipient] = useState<GiftCardRecipient | null>(null);
  const [amountRaw, setAmountRaw] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientMessage, setRecipientMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Bulk compose state.
  const [bulkRecipients, setBulkRecipients] = useState<BatchRecipient[]>([]);
  const [denominationRaw, setDenominationRaw] = useState('');
  const [batchName, setBatchName] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);

  // Shared funding selection (same instrument list drives both flows).
  const [fundingValue, setFundingValue] = useState<string | null>(null);

  const amount = parseGiftCardAmount(amountRaw);
  const amountTouched = amountRaw.trim().length > 0;
  const amountError = amountTouched && amount === null;

  const denomination = parseGiftCardAmount(denominationRaw);
  const denominationTouched = denominationRaw.trim().length > 0;
  const denominationError = denominationTouched && denomination === null;
  const bulkOverCap = bulkRecipients.length > GIFT_CARD_BATCH_MAX_COUNT;

  // ── Funding (saved company cards + company wallet), cloned from the give flow ──
  const cardsQuery = useQuery(companiesGetCompanyCardsV2Options());
  const cards = useMemo(() => extractCards(cardsQuery.data), [cardsQuery.data]);
  const walletQuery = useQuery(companiesGetCompanyWalletOptions());
  const wallet = useMemo(() => toWalletFunds(walletQuery.data), [walletQuery.data]);

  // The amount the funding sufficiency check runs against for the ACTIVE compose mode: the single gift
  // amount, or the whole-BATCH total (denomination × recipient count). null when not yet computable.
  const activeAmount =
    composeMode === 'bulk'
      ? denomination !== null && bulkRecipients.length > 0
        ? batchFaceTotalDollars(denomination, bulkRecipients.length)
        : null
      : amount;

  // Seed the funding default once cards/wallet resolve: server-default card, else first card, else the
  // wallet. Only seeds while nothing is selected (never overrides a user choice).
  useEffect(() => {
    if (fundingValue !== null) return;
    const preferred = cards.find((c) => c.isDefault) ?? cards[0];
    if (preferred) setFundingValue(preferred.id);
    else if (walletQuery.isSuccess) setFundingValue(WALLET_VALUE);
  }, [fundingValue, cards, walletQuery.isSuccess]);

  // If the selected card VANISHES from a cards refetch (deleted in another tab), clear the selection so
  // the seed effect re-picks a valid default. KEY-SAFE: gated on BOTH confirm dialogs being CLOSED.
  useEffect(() => {
    if (confirmOpen || batchConfirmOpen) return;
    if (fundingValue === null || fundingValue === WALLET_VALUE) return;
    if (cardsQuery.isSuccess && !cards.some((c) => c.id === fundingValue)) {
      setFundingValue(null);
    }
  }, [confirmOpen, batchConfirmOpen, fundingValue, cards, cardsQuery.isSuccess]);

  const funding: Funding | null = useMemo(() => {
    if (fundingValue === null) return null;
    return fundingValue === WALLET_VALUE
      ? { kind: 'wallet' }
      : { kind: 'card', cardId: fundingValue };
  }, [fundingValue]);

  // Whether the company wallet is KNOWN to cover the active amount (true when unknown / nothing yet).
  const walletCovers =
    activeAmount === null || walletCoversAmount(wallet.availableCents, activeAmount);
  const walletInsufficient = funding?.kind === 'wallet' && activeAmount !== null && !walletCovers;

  const fundingOptions: SelectOption[] = useMemo(
    () => [
      ...cards.map((c) => ({ value: c.id, label: cardLabel(c) })),
      {
        value: WALLET_VALUE,
        label: `Company wallet (${wallet.display})`,
        // Disable the wallet option when it can't cover the active amount (Members cannot deposit).
        disabled: activeAmount !== null && !walletCovers,
      },
    ],
    [cards, wallet.display, activeAmount, walletCovers],
  );

  const fundingLabelText = fundingDisplayLabel(funding, cards, wallet.display);
  const fundingSummaryText = fundingSummaryLabel(funding, cards, wallet);

  const canReview =
    recipient !== null && amount !== null && funding !== null && !walletInsufficient;
  const bulkCanReview =
    bulkRecipients.length >= 1 &&
    !bulkOverCap &&
    denomination !== null &&
    funding !== null &&
    !walletInsufficient;

  // ★ PAGE-OWNED idempotency-key holders (one per page session, above the dialogs which unmount on
  // close), so a maybe-charged gift/batch's attempt-series survives close/reopen.
  const giftKeyHolder = useRef(createGiftCardIdempotencyKeyHolder());
  const batchKeyHolder = useRef(createGiftCardBatchIdempotencyKeyHolder());

  // ── Sent list (the Individual tab) ──
  const sentList = useInfiniteQuery({
    queryKey: SENT_LIST_KEY,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof SENT_LIST_KEY, number>) =>
      fetchSentPage(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < SENT_PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });
  const sentCards = useMemo(
    () => (sentList.data ? sentList.data.pages.flat() : []),
    [sentList.data],
  );

  const invalidateReads = () => {
    void queryClient.invalidateQueries({ queryKey: SENT_LIST_KEY });
    void queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() });
  };

  // A batch mints cards that show in BOTH the batches report AND the individual sent-list, so refresh
  // both lists + the wallet.
  const invalidateBatchReads = () => {
    void queryClient.invalidateQueries({ queryKey: BATCHES_LIST_KEY_PREFIX });
    void queryClient.invalidateQueries({ queryKey: SENT_LIST_KEY });
    void queryClient.invalidateQueries({ queryKey: companiesGetCompanyWalletQueryKey() });
  };

  const resetCompose = () => {
    setRecipient(null);
    setAmountRaw('');
    setRecipientName('');
    setRecipientMessage('');
  };
  const resetBulkCompose = () => {
    setBulkRecipients([]);
    setDenominationRaw('');
    setBatchName('');
    setBulkMessage('');
  };

  const openSingleCompose = () => setComposeMode('single');
  const openBulkCompose = () => setComposeMode('bulk');
  const cancelCompose = () => {
    setComposeMode(null);
    resetCompose();
  };
  const cancelBulkCompose = () => {
    setComposeMode(null);
    resetBulkCompose();
  };

  // Every close resets the compose fields — WITHOUT any success signal. The success toast + panel
  // collapse fire ONLY via onCloseAfterSuccess.
  const handleDialogOpenChange = (open: boolean) => {
    setConfirmOpen(open);
    if (!open) resetCompose();
  };
  const handleBatchDialogOpenChange = (open: boolean) => {
    setBatchConfirmOpen(open);
    if (!open) resetBulkCompose();
  };

  const presentName = recipientName.trim().length > 0 ? recipientName : null;
  const presentMessage = recipientMessage.trim().length > 0 ? recipientMessage : null;
  const presentBatchName = batchName.trim().length > 0 ? batchName : null;
  const presentBulkMessage = bulkMessage.trim().length > 0 ? bulkMessage : null;

  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-gift-cards')}
    >
      <PageHeader
        title="Gift cards"
        subtitle="Send company-funded gift cards to employees on your roster."
      />

      {composeMode === 'single' ? (
        <ComposePanel
          recipient={recipient}
          onSelectRecipient={(r) => {
            setRecipient(r);
            setRecipientName(r.name);
          }}
          onClearRecipient={() => {
            setRecipient(null);
            setRecipientName('');
          }}
          amountRaw={amountRaw}
          onAmountChange={setAmountRaw}
          amount={amount}
          amountError={amountError}
          recipientName={recipientName}
          onRecipientNameChange={setRecipientName}
          recipientMessage={recipientMessage}
          onRecipientMessageChange={setRecipientMessage}
          fundingValue={fundingValue}
          onFundingChange={setFundingValue}
          fundingOptions={fundingOptions}
          fundingLoading={cardsQuery.isPending || walletQuery.isPending}
          walletDisplay={wallet.display}
          walletInsufficient={walletInsufficient}
          canReview={canReview}
          onReview={() => setConfirmOpen(true)}
          onCancel={cancelCompose}
        />
      ) : composeMode === 'bulk' ? (
        <BulkComposePanel
          recipients={bulkRecipients}
          onRecipientsChange={setBulkRecipients}
          maxCount={GIFT_CARD_BATCH_MAX_COUNT}
          denominationRaw={denominationRaw}
          onDenominationChange={setDenominationRaw}
          denomination={denomination}
          denominationError={denominationError}
          batchName={batchName}
          onBatchNameChange={setBatchName}
          recipientMessage={bulkMessage}
          onRecipientMessageChange={setBulkMessage}
          fundingValue={fundingValue}
          onFundingChange={setFundingValue}
          fundingOptions={fundingOptions}
          fundingLoading={cardsQuery.isPending || walletQuery.isPending}
          walletDisplay={wallet.display}
          walletInsufficient={walletInsufficient}
          canReview={bulkCanReview}
          onReview={() => setBatchConfirmOpen(true)}
          onCancel={cancelBulkCompose}
        />
      ) : (
        <div className="flex flex-wrap justify-end gap-12">
          <Button
            appearance="saas"
            tone="secondary"
            size="lg"
            leadingIcon={<Icon icon={Users} size={16} aria-hidden />}
            onClick={openBulkCompose}
            testId="gift-card-bulk-compose-open"
          >
            Send to multiple people
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            leadingIcon={<Icon icon={Gift} size={16} aria-hidden />}
            onClick={openSingleCompose}
            testId="gift-card-compose-open"
          >
            Send a gift card
          </Button>
        </div>
      )}

      <Tabs.Root
        appearance="saas"
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ReportTab)}
        testId="gift-card-report-tabs"
      >
        <Tabs.List aria-label="Gift-card reports">
          <Tabs.Tab value="individual" testId="gift-card-tab-individual">
            Gift cards
          </Tabs.Tab>
          <Tabs.Tab value="batches" testId="gift-card-tab-batches">
            Batches
          </Tabs.Tab>
          {occasionProgramsEnabled && (
            <Tabs.Tab value="programs" testId="gift-card-tab-programs">
              Programs
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="individual">
          <SentList
            cards={sentCards}
            loading={sentList.isPending}
            error={
              sentList.isError
                ? isGivmoError(sentList.error)
                  ? sentList.error.message
                  : 'Failed to load gift cards.'
                : undefined
            }
            onRetry={() => void sentList.refetch()}
            hasMore={sentList.hasNextPage === true}
            loadingMore={sentList.isFetchingNextPage}
            onLoadMore={() => void sentList.fetchNextPage()}
            onSendCta={openSingleCompose}
          />
        </Tabs.Panel>

        <Tabs.Panel value="batches">
          <BatchReport
            onComposeCta={openBulkCompose}
            focusBatchId={focusBatchId}
            onFocusConsumed={clearFocusBatch}
          />
        </Tabs.Panel>

        {occasionProgramsEnabled && (
          <Tabs.Panel value="programs">
            <OccasionProgramsPanel
              onOpenBatch={(batchId) => {
                setFocusBatchId(batchId);
                setActiveTab('batches');
              }}
            />
          </Tabs.Panel>
        )}
      </Tabs.Root>

      {recipient !== null && amount !== null && funding !== null && (
        <SendGiftCardDialog
          open={confirmOpen}
          onOpenChange={handleDialogOpenChange}
          recipient={recipient}
          amount={amount}
          funding={funding}
          fundingLabel={fundingLabelText}
          fundingSummary={fundingSummaryText}
          recipientName={presentName}
          recipientMessage={presentMessage}
          keyHolder={giftKeyHolder.current}
          onSuccess={invalidateReads}
          onCloseAfterSuccess={() => {
            toast.success('Gift card sent');
            setComposeMode(null);
          }}
          onCloseAfterAttempt={invalidateReads}
        />
      )}

      {bulkRecipients.length > 0 && denomination !== null && funding !== null && (
        <SendGiftCardBatchDialog
          open={batchConfirmOpen}
          onOpenChange={handleBatchDialogOpenChange}
          recipients={bulkRecipients}
          denomination={denomination}
          funding={funding}
          fundingLabel={fundingLabelText}
          fundingSummary={fundingSummaryText}
          batchName={presentBatchName}
          recipientMessage={presentBulkMessage}
          keyHolder={batchKeyHolder.current}
          onSuccess={invalidateBatchReads}
          // Success-close ONLY: toast, collapse the compose panel, and surface the new batch in the
          // Batches tab. A plain Cancel / error close must never claim "Batch sent".
          onCloseAfterSuccess={() => {
            toast.success('Batch sent');
            setComposeMode(null);
            setActiveTab('batches');
          }}
          onCloseAfterAttempt={invalidateBatchReads}
        />
      )}
    </div>
  );
}

// ── Compose panel (recipient → amount + message → funding → Review) ──────────────────────────────

function ComposePanel({
  recipient,
  onSelectRecipient,
  onClearRecipient,
  amountRaw,
  onAmountChange,
  amount,
  amountError,
  recipientName,
  onRecipientNameChange,
  recipientMessage,
  onRecipientMessageChange,
  fundingValue,
  onFundingChange,
  fundingOptions,
  fundingLoading,
  walletDisplay,
  walletInsufficient,
  canReview,
  onReview,
  onCancel,
}: {
  recipient: GiftCardRecipient | null;
  onSelectRecipient: (r: GiftCardRecipient) => void;
  onClearRecipient: () => void;
  amountRaw: string;
  onAmountChange: (v: string) => void;
  amount: number | null;
  amountError: boolean;
  recipientName: string;
  onRecipientNameChange: (v: string) => void;
  recipientMessage: string;
  onRecipientMessageChange: (v: string) => void;
  fundingValue: string | null;
  onFundingChange: (v: string | null) => void;
  fundingOptions: SelectOption[];
  fundingLoading: boolean;
  walletDisplay: string;
  walletInsufficient: boolean;
  canReview: boolean;
  onReview: () => void;
  onCancel: () => void;
}) {
  // Who adds funds, and where, is this host's (EP-MNT5(h)).
  const { words } = useEmployerModulePaths();
  return (
    <div className="flex flex-col gap-24" {...testIdProp('gift-card-compose')}>
      {/* ── Step 1: recipient ── */}
      <Card>
        <CardHeader title="Who is it for?" subtitle="Pick an employee from your roster." />
        <CardBody>
          <GiftCardRecipientPicker
            selected={recipient}
            onSelect={onSelectRecipient}
            onClear={onClearRecipient}
          />
        </CardBody>
      </Card>

      {/* ── Step 2: amount + optional name/message ── */}
      <Card>
        <CardHeader
          title="How much?"
          subtitle={`Whole dollars, ${GIFT_CARD_RANGE_LABEL}. No fees — the gift is worth its full face value.`}
        />
        <CardBody className="flex flex-col gap-16">
          <div className="flex flex-wrap gap-8" {...testIdProp('gift-card-presets')}>
            {GIFT_CARD_PRESETS.map((preset) => {
              const active = amount === preset;
              return (
                <Button
                  key={preset}
                  appearance="saas"
                  tone={active ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onAmountChange(String(preset))}
                  testId={`gift-card-preset-${preset}`}
                >
                  {formatGiftCardDollars(preset)}
                </Button>
              );
            })}
          </div>

          <TextField
            label="Amount (USD)"
            inputMode="numeric"
            placeholder="0"
            value={amountRaw}
            onChange={(e) => onAmountChange(e.target.value)}
            error={amountError}
            errorMessage={
              amountError ? `Enter a whole-dollar amount from ${GIFT_CARD_RANGE_LABEL}.` : undefined
            }
            className="max-w-[220px]"
            testId="gift-card-amount-input"
          />

          <TextField
            label="Recipient name (optional)"
            placeholder="Their name"
            value={recipientName}
            maxLength={NAME_MAX}
            onChange={(e) => onRecipientNameChange(e.target.value)}
            className="max-w-[360px]"
            testId="gift-card-recipient-name"
          />

          <Textarea
            label="Message (optional)"
            placeholder="Add a personal note…"
            value={recipientMessage}
            maxLength={MESSAGE_MAX}
            onChange={(e) => onRecipientMessageChange(e.target.value)}
            helper={`${recipientMessage.length}/${MESSAGE_MAX}`}
            className="max-w-[480px]"
            testId="gift-card-message"
          />
        </CardBody>
      </Card>

      {/* ── Step 3: funding ── */}
      <Card>
        <CardHeader
          title="Pay with"
          subtitle="Charge a company card or debit the company wallet."
        />
        <CardBody className="flex flex-col gap-12">
          {fundingLoading ? (
            <Skeleton className="h-[40px] w-full max-w-[360px] rounded-lg" />
          ) : (
            <Select
              label="Funding source"
              options={fundingOptions}
              value={fundingValue}
              onValueChange={onFundingChange}
              className="max-w-[360px]"
              testId="gift-card-payment-select"
            />
          )}

          {walletInsufficient && (
            <div
              className="flex items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12"
              {...testIdProp('gift-card-wallet-insufficient')}
            >
              <Icon icon={WalletIcon} size={16} aria-hidden className="mt-1 text-danger-700" />
              <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                Insufficient wallet balance ({walletDisplay}) — {words.giftCardAddFunds}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Review → the money-moving confirm dialog ── */}
      <div className="flex items-center justify-end gap-12">
        <Button
          appearance="saas"
          tone="ghost"
          size="lg"
          onClick={onCancel}
          testId="gift-card-compose-cancel"
        >
          Cancel
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          disabled={!canReview}
          onClick={onReview}
          testId="gift-card-review-submit"
        >
          {amount !== null ? `Review — send ${formatGiftCardDollars(amount)}` : 'Review gift card'}
        </Button>
      </div>
    </div>
  );
}

// ── Sent list ─────────────────────────────────────────────────────────────────────────────────────

function SentList({
  cards,
  loading,
  error,
  onRetry,
  hasMore,
  loadingMore,
  onLoadMore,
  onSendCta,
}: {
  cards: CompanyGiftCardOut[];
  loading: boolean;
  error: string | undefined;
  onRetry: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onSendCta: () => void;
}) {
  const columns: DataTableColumn<CompanyGiftCardOut>[] = useMemo(
    () => [
      {
        id: 'recipient',
        header: 'Recipient',
        width: '34%',
        cell: (card) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-w600 text-text-strong">
              {card.recipient_name?.trim() || card.recipient_email?.trim() || '—'}
            </span>
            {card.recipient_email && card.recipient_name?.trim() && (
              <span className="truncate text-s12 text-text-tertiary">{card.recipient_email}</span>
            )}
          </div>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        width: '120px',
        cell: (card) => {
          // Face value + (card path only) the exact GROSS charged, surfaced from the response (EP-FIX3).
          // Null for a wallet send + every batch-minted card (the batch is the charge grain) → face only,
          // never "$0".
          const charged = formatGiftCardCharged(card.amount_charged);
          return (
            <div className="flex flex-col">
              <span className="tabular-nums text-text-strong">
                {formatGiftCardAmount(card.amount)}
              </span>
              {charged !== null && (
                <span
                  className="tabular-nums text-s12 text-text-tertiary"
                  {...testIdProp(`gift-card-charged-${card.id}`)}
                >
                  Charged {charged}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'funding',
        header: 'Funding',
        width: '140px',
        cell: (card) => <span className="text-text-secondary">{fundingLabel(card.funding)}</span>,
      },
      {
        id: 'sent',
        header: 'Sent',
        width: '120px',
        cell: (card) => (
          <span className="text-text-secondary">{toDateOnly(card.created_at) || '—'}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '160px',
        cell: (card) => <GiftCardStatusCell card={card} />,
      },
    ],
    [],
  );

  return (
    <DataTable
      columns={columns}
      rows={cards}
      rowKey={(c) => c.id}
      rowTestId={(c) => `gift-card-row-${c.id}`}
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyState={
        <EmptyState
          testId="gift-card-empty-state"
          icon={<Icon icon={Gift} size={20} aria-hidden />}
          title="No gift cards sent yet"
          description="Gift cards you send to employees will appear here."
          action={
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              onClick={onSendCta}
              testId="gift-card-empty-send"
            >
              Send a gift card
            </Button>
          }
        />
      }
      footer={
        hasMore ? (
          <>
            <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
              Showing {cards.length} gift cards
            </span>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={loadingMore}
              onClick={onLoadMore}
              testId="gift-card-load-more"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </>
        ) : undefined
      }
    />
  );
}
